import { Component, NgZone, OnDestroy, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subject, Subscription, takeUntil } from 'rxjs';
import { EntityType, EntityTypePlural } from 'src/app/enums/entity-type.enum';
import { PlasterSampleDto } from 'src/app/models/plaster-sample-dto';
import { ApiService } from 'src/app/services/api.service';
import { LanguageService } from 'src/app/services/language.service';
import { RoutingService } from 'src/app/services/routing.service';
import { ControlType } from '../arcgis-map/constants/control-type.enum';
import { MapControlItem } from '../arcgis-map/constants/map-control-item.interface';
import { ARCGIS_MAP_LIFECYCLE } from '../arcgis-map/constants/map-life-cycle.interface';
import { MainMapService } from '../arcgis-map/services/main-map.service';

@Component({
  selector: 'plaster-sample',
  templateUrl: './plaster-sample.component.html',
  styleUrls: ['./plaster-sample.component.scss'],
  providers: [
    {
      provide: ARCGIS_MAP_LIFECYCLE,
      useClass: MainMapService,
    },
  ]
})
export class PlasterSampleComponent implements OnInit, OnDestroy {
  private entityDetailsSubscription?: Subscription;
  closeClicked: Subject<void> = new Subject<void>();

  selectedSample!: PlasterSampleDto;
  sampleIsLoading = true;
  entityId = -1;
  shapes: any[] = [];
  zoomShapeIds: string[] = [];
  currentLanguage$!: Observable<string>;

  constructor(
    private routingService: RoutingService,
    private apiService: ApiService,
    private translate: TranslateService,
    private languageService: LanguageService,
    private ngZone: NgZone) {
  }

  mapControls: MapControlItem[] = [
    { position: 'top-left', type: ControlType.FullScreen },
    { position: 'top-left', type: ControlType.Zoom },
    {
      position: 'top-left',
      type: ControlType.BasemapGallery,
      expand: {
        id: 'basemap-gallery-expand',
        position: 'top-left',
        expandIcon: 'basemap',
        expandTooltip: `${this.translate.instant('iaa.map.expand-basemap-gallery')}`,
        mode: 'floating',
      },
    },
    {
      position: 'top-left',
      type: ControlType.LayerList,
      expand: {
        id: 'layer-list-expand',
        position: 'top-left',
        expandIcon: 'layers',
        expandTooltip: `${this.translate.instant('iaa.map.expand-layers')}`,
        mode: 'floating',
      },
    },
  ];

  ngOnInit(): void {
    this.currentLanguage$ = this.languageService.currentLanguage$;

    this.entityDetailsSubscription = this.routingService.entityDetails$
      .subscribe(entityDetails => {
        this.ngZone.run(() => {
          const { id, type } = entityDetails;
          if (!!id && type === EntityType.PLASTER_SAMPLE) {
            this.entityId = id;
            this.getSampleDetails(id);
          }
        });
      });
  }

  ngOnDestroy(): void {
    this.entityDetailsSubscription?.unsubscribe();
  }

  getDetailsTitle(): string {
    const name = this.selectedSample?.sampleName || this.selectedSample?.sampleId || '';
    return `${this.translate.instant('iaa.app.plasterSample.sample')} ${name}`;
  }

  private getSampleDetails(entityId: number) {
    this.sampleIsLoading = true;
    this.apiService.getPlasterSampleDetails(entityId)
      .pipe(takeUntil(this.closeClicked))
      .subscribe({
        next: (res: PlasterSampleDto) => {
          this.selectedSample = res;
          this.sampleIsLoading = false;
          this.buildShapes();
        },
        error: () => this.sampleIsLoading = false,
      });
  }

  // A sample has no geometry of its own - the map draws the locus it was taken from
  // (highlighted) over the rest of the excavation's loci.
  private buildShapes() {
    const selected = this.convertShapesArray(this.selectedSample?.mapInformation?.selectedLocusShapes || []);
    const chafira = this.convertShapesArray(this.selectedSample?.mapInformation?.chafiraLocusShapes || []);

    selected.forEach(shape => {
      shape.styledParams.fillColor = [106, 90, 205, 0.5];
      shape.styledParams.borderColor = [106, 90, 205];
    });
    chafira.forEach(shape => {
      shape.styledParams.fillColor = [255, 180, 238, 0.5];
      shape.styledParams.borderColor = [255, 180, 238];
    });

    this.shapes = [...chafira, ...selected];
    this.zoomShapeIds = selected.map(shape => shape.shapeId);
  }

  private convertShapesArray(shapes: any[]): any[] {
    return shapes.map(shape => this.convertToConvertedShape(shape));
  }

  private convertToConvertedShape(shape: any): any {
    const entity = shape.shapeData.entity;
    return {
      shapeId: shape.shapeId.toString(),
      wkt: shape.shapeWkt,
      layerName: "שכבת תוצאות שאילתה",
      hidden: false,
      isExtent: true,
      shapeData: {
        entity: {
          entityId: entity.entityId,
          entityType: entity.entityType
        },
      },
      styledParams: {
        geometryType: "Polygon",
        fillColor: [0, 0, 0, 0],
        borderColor: [0, 0, 0, 0],
        borderWidth: 2,
      },
    };
  }

  goToEntity(entityId: number, entityType: string): void {
    this.routingService.navigateToEntity(entityId, entityType);
  }

  get hasLicense(): boolean {
    return !!this.selectedSample?.licenses?.length;
  }

  // Closes the sample page and re-runs the search filtered to this sample's licence,
  // so the user lands on every other plaster sample from the same excavation.
  goToOtherSamples(): void {
    const licenseId = this.selectedSample?.licenses?.[0]?.licenseId;
    if (!licenseId) return;

    this.routingService.navigateWithMerge({
      entityType: undefined,
      entityId: undefined,
      licenses: licenseId,
      entityTypes: EntityTypePlural.PLASTER_SAMPLES,
    });
  }
}
